'use client';

import { useMemo, useCallback } from 'react';
import { Button, buttonVariants } from '@/components/ui/button';
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { UrlSection } from './UrlSection';
import { TextSection } from './TextSection';
import { VCardSection } from './VcardSection';
import { EditVCardSection } from './EditVCardSection';
import { WiFiSection } from './WiFiSection';
import { getDefaultContentByType, type TQrCodeContentType } from '@shared/schemas';
import { useQrCodeGeneratorStore } from '@/components/provider/QrCodeConfigStoreProvider';
import { useTranslations } from 'next-intl';
import { EditUrlSection } from './EditUrlSection';
import { BulkImport } from './BulkImport';
import { EmailSection } from './EmailSection';
import { LocationSection } from './LocationSection';
import { EventSection } from './EventSection';
import { EpcSection } from './EpcSection';
import { CONTENT_TYPE_CONFIGS } from '@/lib/content-type.config';
import { DynamicBadge } from '../DynamicBadge';
import { useAuth } from '@/components/provider/AuthProvider';

type ContentSwitchProps = {
	hiddenTabs?: TQrCodeContentType[];
	isEditMode?: boolean;
	compact?: boolean;
};

type TabConfig<T extends TQrCodeContentType = TQrCodeContentType> = {
	type: T;
	label: string;
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
	hidden?: boolean;
	enableBulk: boolean;
};

// Stable component map - prevents recreation on every render
const CONTENT_COMPONENTS = {
	url: { edit: EditUrlSection, view: UrlSection },
	text: { edit: TextSection, view: TextSection },
	wifi: { edit: WiFiSection, view: WiFiSection },
	vCard: { edit: EditVCardSection, view: VCardSection },
	email: { edit: EmailSection, view: EmailSection },
	location: { edit: LocationSection, view: LocationSection },
	event: { edit: EventSection, view: EventSection },
	epc: { edit: EpcSection, view: EpcSection },
} as const;

const TABS: TabConfig[] = CONTENT_TYPE_CONFIGS.map((config) => ({
	type: config.type,
	label: config.label,
	icon: config.icon,
	enableBulk: config.enableBulk,
}));

export const ContentSwitch = ({ hiddenTabs = [], isEditMode, compact }: ContentSwitchProps) => {
	const t = useTranslations('generator.contentSwitch');
	const { isSignedIn } = useAuth();

	const { content, updateContent, bulkMode, updateBulkMode } = useQrCodeGeneratorStore(
		(state) => state,
	);

	const activeTab = TABS.find((t) => t.type === content.type);
	const bulkAllowed = activeTab?.enableBulk;

	// Memoize visibleTabs to prevent unnecessary recalculation
	const visibleTabs = useMemo(
		() => TABS.filter((tab) => !hiddenTabs.includes(tab.type)),
		[hiddenTabs],
	);

	// Stable callback for content updates
	const handleContentUpdate = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- discriminated union data varies by type
		(type: TQrCodeContentType, data: any) => {
			updateContent({ type, data });
		},
		[updateContent],
	);

	const handleTabChange = useCallback(
		(value: string) => {
			updateContent(getDefaultContentByType(value as TQrCodeContentType, isSignedIn === true));
		},
		[updateContent, isSignedIn],
	);

	// Create stable onChange callbacks for each content type
	const onChangeCallbacks = useMemo(() => {
		const callbacks = {} as Record<TQrCodeContentType, (data: unknown) => void>;
		visibleTabs.forEach((tab) => {
			callbacks[tab.type] = (data: unknown) => handleContentUpdate(tab.type, data);
		});
		return callbacks;
	}, [visibleTabs, handleContentUpdate]);

	return (
		<Tabs
			defaultValue={content.type}
			value={content.type}
			className="max-w-[650px]"
			suppressHydrationWarning
			onValueChange={handleTabChange}
		>
			{/* Compact mode in edit: hide selector (type is fixed). Compact mode otherwise: select dropdown. Normal mode: tab grid */}
			{compact && isEditMode ? null : compact ? (
				<div className="mb-6">
					<Select value={content.type} onValueChange={handleTabChange}>
						<SelectTrigger className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{visibleTabs.map(({ type, icon: Icon, label }) => (
								<SelectItem key={type} value={type} className="pl-2 [&>span:first-child]:hidden">
									<div className="flex items-center gap-2">
										<Icon className="h-5 w-5" />
										<span>{t(`tab.${label}`)}</span>
									</div>
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			) : (
				<TabsList
					className={`mb-6 grid h-auto grid-cols-2 gap-2 bg-transparent p-0 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-4`}
				>
					{visibleTabs.map(({ type, icon: Icon, label }) => (
						<TabsTrigger key={type} value={type} asChild>
							<button className={buttonVariants({ variant: 'tab' })}>
								<Icon className="mr-2 h-6 w-6 min-w-5" />
								{t(`tab.${label}`)}
							</button>
						</TabsTrigger>
					))}
				</TabsList>
			)}

			{/* Bulk Header */}
			{!isEditMode && bulkAllowed && (
				<div className="flex justify-between mb-4 items-center">
					{/* Dynamic Badge - For URL and vCard types */}
					{content.type === 'url' ? (
						<DynamicBadge
							checked={content.data.isDynamic ?? true}
							onChange={(checked) => {
								updateContent({
									type: 'url',
									data: {
										...content.data,
										isDynamic: checked,
									},
								});
							}}
						/>
					) : content.type === 'vCard' ? (
						<DynamicBadge
							checked={content.data.isDynamic ?? true}
							onChange={(checked) => {
								updateContent({
									type: 'vCard',
									data: {
										...content.data,
										isDynamic: checked,
									},
								});
							}}
						/>
					) : (
						<div></div>
					)}

					<div className="hidden sm:block">
						{bulkMode.isBulkMode ? (
							<Button variant="link" onClick={() => updateBulkMode(false)}>
								{t('cancel')}
							</Button>
						) : (
							<Button variant="link" className="p-0" onClick={() => updateBulkMode(true)}>
								<DocumentArrowUpIcon className="sm:mr-1.5 h-8 w-8 sm:h-6 sm:w-6" />
								<span>{t('bulkModeBtn')}</span>
							</Button>
						)}
					</div>
				</div>
			)}

			{/* Content */}
			{bulkMode.isBulkMode && bulkAllowed ? (
				<BulkImport contentType={content.type} />
			) : (
				visibleTabs.map((tab) => {
					const Component = CONTENT_COMPONENTS[tab.type][isEditMode ? 'edit' : 'view'];
					return (
						<TabsContent key={tab.type} value={tab.type} className="pt-2">
							{/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- discriminated union data varies by content type */}
							<Component value={content.data as any} onChange={onChangeCallbacks[tab.type]} />
						</TabsContent>
					);
				})
			)}
		</Tabs>
	);
};
